import React from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext'; // <-- Import currency context
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PlaceIcon from '@mui/icons-material/Place';

// Assuming you have this Rating component available in your project
import Rating from '../components/Rating'; 

// --- Copied from your Trips component for consistency ---
const PALETTE = {
  stoneWhite: '#FCF9F3',
  peacockTeal: '#005B5C',
  saffronOrange: '#FCA510',
  textPrimary: '#1A2027',
  textSecondary: '#5F5F5F',
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.07)',
  transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  overflow: 'hidden',
  textDecoration: 'none',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '& .destination-overlay': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
}));

const DestinationOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%', // Cover the whole image area
  padding: theme.spacing(2),
  color: 'white',
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.2) 100%)',
  backdropFilter: 'blur(3px)',
  transform: 'translateY(100%)',
  opacity: 0,
  transition: 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out',
  overflowY: 'auto',
}));

// Helper function to get currency symbol
const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode?.toLowerCase()) {
    case 'eur': return '€';
    case 'gbp': return '£';
    case 'usd':
    default: return '$';
  }
};
// --- End of copied assets ---

const RelatedItemCard = ({ item, type }) => {
  const { currency } = useCurrency();

  // ----- RENDER A FULL-FEATURED TRIP CARD -----
  if (type === 'trip') {
    const linkTo = `/trips/${item.slug}`;
    const imageUrl = item.images?.[0] || ''; // Fallback for safety
    const title = item.title;

    // Extract trip-specific details with safe fallbacks
    const uniqueDestinations = [...new Set(
      item.itinerary
        ?.map(day => day.destination?.name)
        .filter(Boolean)
    )];
    const price = item.basePrice?.[currency] || item.basePrice?.usd || 0;
    const symbol = getCurrencySymbol(currency);

    return (
      <StyledCard component={Link} to={linkTo}>
        <Box sx={{ position: 'relative', height: '220px' }}>
          <CardMedia component="img" height="220" image={imageUrl} alt={title} />
          <DestinationOverlay className="destination-overlay">
            <Typography variant="h6" fontWeight="bold" gutterBottom>Key Destinations</Typography>
            <List dense disablePadding>
              {uniqueDestinations.slice(0, 4).map((name, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemIcon sx={{ minWidth: '32px' }}><PlaceIcon sx={{ color: 'white', fontSize: '1rem' }} /></ListItemIcon>
                  <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                </ListItem>
              ))}
            </List>
          </DestinationOverlay>
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          <Typography variant="body2" color="primary" fontWeight="bold">
            {item.duration} Days • {item.tripType?.name}
          </Typography>
          <Typography gutterBottom variant="h5" component="h3" sx={{ fontWeight: 600, color: '#005F69', mt: 1, mb: 2 }}>
            {title}
          </Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, color: PALETTE.saffronOrange }}>
            From {symbol}{price.toLocaleString()} / person
          </Typography>
          <Typography variant="body2" color={PALETTE.textSecondary} sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden', textOverflow: 'ellipsis', mt: 1 }}>
            {item.summary}
          </Typography>
          {item.ratings && <Rating value={item.totalRatings} text={`(${item.ratings.length})`} />}
        </CardContent>
        <CardActions sx={{ px: 3, pb: 3, mt: 'auto' }}>
          <Button variant="contained" fullWidth size="large" sx={{ backgroundColor: PALETTE.peacockTeal, fontWeight: 600, py: 1.5, '&:hover': { backgroundColor: '#004243' } }}>
            View Itinerary
          </Button>
        </CardActions>
      </StyledCard>
    );
  }

  // ----- RENDER A SIMPLER, STYLED BLOG CARD -----
  const linkTo = `/blogs/${item._id}`;
  const imageUrl = item.featuredImage;
  const title = item.title;

  return (
    <StyledCard component={Link} to={linkTo}>
      <CardMedia component="img" height="220" image={imageUrl} alt={title} />
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="body2" color="primary" component="div" fontWeight="bold">
          Blog Post
        </Typography>
        <Typography variant="h5" component="h3" sx={{ fontWeight: 600, color: '#005F69', mt: 1 }}>
          {title}
        </Typography>
        {item.summary && (
          <Typography variant="body2" color={PALETTE.textSecondary} sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 4, overflow: 'hidden', textOverflow: 'ellipsis', mt: 2 }}>
            {item.summary}
          </Typography>
        )}
      </CardContent>
       <CardActions sx={{ px: 3, pb: 3, mt: 'auto' }}>
          <Button variant="contained" fullWidth size="large" sx={{ backgroundColor: PALETTE.peacockTeal, fontWeight: 600, py: 1.5, '&:hover': { backgroundColor: '#004243' } }}>
            Read More
          </Button>
        </CardActions>
    </StyledCard>
  );
};

export default RelatedItemCard;